import { Chip } from "@mui/material";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { MutableRefObject, memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TbUserCancel } from "react-icons/tb";

import CommonTableToolbar from "@/components/CommonTableToolbar";
import ExportTable from "@/components/ExportTable";
import FilterButton from "@/components/table/FilterButton";
import { LocaleStorageKeys } from "@/constants";
import { useExportClinicUnassignedPassengersMutation } from "@/requests/hotel-clinic.queries";
import {
    useClinicUnassignedCancelledFilter,
    useClinicUnassignedIsFullScreen,
    useClinicUnassignedPassengersTableActions,
    useHotelClinicArrivalDate,
} from "@/stores/use-clinic-arrival-unassigned-passengers-table";
import { ExportTypes } from "@/types/export.types";
import { CommonStatusItem } from "@/types/global";
import { HotelPickupReasons } from "@/types/hotel.types";

import { HotelClinicListItem } from "../../types";
import HotelClinicArrivalSectionTooltip from "../HotelClinicArrivalSectionTitle";
import ClinicUnassignedChangesButton from "./ClinicUnassignedChangesButton";
import { defaultUnAssignedPassengersTableGridOptions } from "./defaultUnassignedPassengersGridOptions";

const newColumnNames = ["route"];

const UnassignedPassengersToolbar = memo(function UnassignedPassengersToolbar({
    apiRef,
    rows,
    ...commonStatusProps
}: {
    apiRef: MutableRefObject<GridApiPremium>;
    rows: HotelClinicListItem[];
} & CommonStatusItem) {
    const { t } = useTranslation();
    const cancelledPassengersFilterActive = useClinicUnassignedCancelledFilter();
    const { toggleCancelledPassengersFilter, updateFullScreen } = useClinicUnassignedPassengersTableActions();
    const isFullScreen = useClinicUnassignedIsFullScreen();
    const clinicArrivalDate = useHotelClinicArrivalDate();
    const { mutateAsync: exportUnassignedMutate, isPending: exportUnassignedLoading } =
        useExportClinicUnassignedPassengersMutation();

    const statusesTexts = useMemo(
        () =>
            Object.keys(HotelPickupReasons).reduce<{ [key in HotelPickupReasons]: string }>(
                (prev, curr) => {
                    prev[HotelPickupReasons[curr as keyof typeof HotelPickupReasons]] = t(`hotelPickupReasons.${curr}`);
                    return prev;
                },
                {} as { [key in HotelPickupReasons]: string }
            ),
        [t]
    );

    const handleUnassignedExportRequest = useCallback(
        async (format: ExportTypes) => {
            if (exportUnassignedLoading) return undefined;
            return exportUnassignedMutate({
                date: clinicArrivalDate,
                format,
                ids: [],
                statusesTexts,
                cancelled: cancelledPassengersFilterActive,
            });
        },
        [
            cancelledPassengersFilterActive,
            clinicArrivalDate,
            exportUnassignedLoading,
            exportUnassignedMutate,
            statusesTexts,
        ]
    );

    return (
        <CommonTableToolbar
            apiRef={apiRef}
            isFullScreen={isFullScreen}
            onFullScreenToggle={() => updateFullScreen()}
            storageKey={LocaleStorageKeys.CLINIC_ARRIVAL_UNASSIGNED_GRID_SETTINGS}
            defaultOptions={defaultUnAssignedPassengersTableGridOptions}
            hideQuickFilter
            newColumnNames={newColumnNames}
            TitleComponent={
                <>
                    <HotelClinicArrivalSectionTooltip label="Atanmamış Yolcular" {...commonStatusProps} />
                    <Chip
                        sx={{
                            backgroundColor: commonStatusProps.bg,
                            color: commonStatusProps.text,
                            fontWeight: 500,
                            fontSize: "12px !important",
                        }}
                        size="small"
                        className="ml-1"
                        label={rows.length + " " + "Yolcu"}
                    />
                </>
            }
        >
            <ClinicUnassignedChangesButton apiRef={apiRef} />

            <FilterButton
                active={cancelledPassengersFilterActive}
                handleClick={toggleCancelledPassengersFilter}
                icon={TbUserCancel}
                tooltip={t("tooltips.cancelled", {
                    count: cancelledPassengersFilterActive ? 2 : 1,
                })}
            />
            <ExportTable
                fileName="clinicUnassignedPassengersExport"
                filePrefix={() => clinicArrivalDate.toFormat("dd.MM.yyyy")}
                handleRequest={handleUnassignedExportRequest}
                loading={exportUnassignedLoading}
            />
        </CommonTableToolbar>
    );
});

export default UnassignedPassengersToolbar;
